import './styles/tokens.css';
import './styles/base.css';
import './styles/components.css';
import './styles/mobile.css';

import { bootRouter, setRoute, getRoute, onRouteChange } from './router';
import { getTheme, setTheme, getTenant } from './theme';
import { getSession } from './auth';
import { renderTopbar, bindTopbarActions } from './components/topbar';
import { renderRail, bindRailClicks, renderBottomSheet } from './components/rail';
import { bindDrawerClicks, closeDrawer } from './components/drawer';
import { renderLogin, bindLogin } from './screens/login';
import { renderOverview } from './screens/overview';
import { renderCourses, bindCourses } from './screens/courses';
import { renderCourseDetail, bindCourseDetail } from './screens/course-detail';
import { renderBatches, bindBatches } from './screens/batches';
import { renderStudents, bindStudents } from './screens/students';
import { renderAttendance, bindAttendance } from './screens/attendance';
import { renderPipeline, bindPipeline } from './screens/pipeline';
import { renderTreasury } from './screens/treasury';
import { renderCommerce, bindCommerce } from './screens/commerce';
import { renderTreasuryCommerce, bindTreasuryCommerce } from './screens/treasury-commerce';
import { renderSiteContent, bindSiteContent } from './screens/site-content';
import { renderMembers, bindMembers } from './screens/members';
import { renderPayroll, bindPayroll } from './screens/payroll';
import { renderPayrollHistory, bindPayrollHistory } from './screens/payroll-history';
import { renderStaffAttendance, bindStaffAttendance } from './screens/staff-attendance';
import { renderSettings, bindSettings } from './screens/settings';
import { renderLeadsInbox, bindLeadsInbox } from './screens/leads-inbox';
import { toast } from './components/toast';
import { registerPWA } from './pwa';
import { NAV_BY_ACCESS_ROLE, NAV_BY_ROLE, type Role, type ProductRole } from './config';

registerPWA();
(window as any).__repaint ??= () => paint();

const el = (id: string) => document.getElementById(id) as HTMLElement | null;

const ROUTE_RENDERERS: Record<string, () => string> = {
  overview:        () => renderOverview(),
  courses:         renderCourses,
  'course-detail': (): string => { const m = (location.hash.match(/course-detail\/([^/?]+)/) || [, ''])[1]; return renderCourseDetail(decodeURIComponent(m)); },
  batches:         renderBatches,
  students:        renderStudents,
  attendance:      renderAttendance,
  pipeline:        renderPipeline,
  treasury:        renderTreasury,
  commerce:        renderCommerce,
  'commerce-finance': renderTreasuryCommerce,
  'site-content': renderSiteContent,
  payroll:         renderPayroll,
  'payroll-history': renderPayrollHistory,
  'staff-attendance': renderStaffAttendance,
  members:         renderMembers,
  settings:        renderSettings,
  'leads-inbox':   renderLeadsInbox,
};

// Re-render ONLY the current screen's canvas (no full-shell repaint → no flicker).
(window as any).__rerender = () => {
  const main = document.querySelector<HTMLElement>('main.canvas');
  if (!main) return;
  const fn = ROUTE_RENDERERS[getRoute()];
  if (!fn) return;
  main.innerHTML = fn();
  animateKPIs();
};

function animateKPIs() {
  document.querySelectorAll<HTMLElement>('[data-count]').forEach((node) => {
    const target = parseInt(node.dataset.count ?? '0', 10);
    if (!target) return;
    const dur = 900;
    const start = performance.now();
    function tick(now: number) {
      const p = Math.min((now - start) / dur, 1);
      const v = Math.round(target * (1 - Math.pow(1 - p, 3)));
      node.textContent = v.toLocaleString('en-IN');
      if (p < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  });
}

function renderShell() {
  const session = getSession();
  if (!session) return;
  const theme = getTheme();
  const active = getRoute();
  const tenantId = document.body.dataset.tenant || getTenant();

  // Route-level permission gate: hiding sidebar items is not enough because a
  // user can type a hash route manually.
  const accessRole = session.role as ProductRole;
  const accessNav = NAV_BY_ACCESS_ROLE[accessRole] || NAV_BY_ROLE[session.role as Role] || NAV_BY_ROLE.admin;
  const navRole: Role = accessRole === 'learner' ? 'learner' : accessRole === 'educator' ? 'instructor' : 'admin';
  // 'course-detail' is an underlay of 'courses' — allow it whenever courses is allowed.
  const coursesAllowed = accessNav.some((section) => section.items.some((item) => item.id === 'courses'));
  const allowed = active === 'course-detail'
    ? coursesAllowed
    : accessNav.some((section) => section.items.some((item) => item.id === active));
  if (!allowed) { setRoute('overview'); return; }

  const section = ROUTE_RENDERERS[active] ? ROUTE_RENDERERS[active]() : '';
  if (!section) { setRoute('overview'); return; }

  (window as any).__repaint = () => paint();

  const app = el('app');
  if (!app) return;
  app.innerHTML = `
    <div class="app">
      ${renderTopbar(theme)}
      <aside class="rail" aria-label="Workspace">
        <div style="padding: 0 var(--s-3) var(--s-4);">
          <div class="t-eyebrow">Working as</div>
          <div class="t-h2" style="margin-top:4px;">${tenantId}</div>
          <div class="t-meta" style="margin-top:var(--s-1); font-size: 11px;">${navigator.onLine ? 'connected' : 'offline'}</div>
        </div>
        ${renderRail(navRole, active)}
      </aside>
      <main class="canvas" role="main">${section}</main>
      ${renderBottomSheet(active)}
    </div>

    <div class="drawer-scrim" id="scrim" data-action="close-drawer"></div>
    <aside class="drawer" id="drawer-lead" role="dialog" aria-label="Lead detail">
      <div class="drawer-head">
        <div class="avatar avatar-lg av-c5" id="lead-avatar">S</div>
        <div style="flex:1; min-width:0;">
          <div class="t-h2" id="lead-name">—</div>
          <div class="t-meta">Unknown · +91 98xxx xxxxx</div>
        </div>
        <span class="pill pill-warn"><span class="dot"></span> Interested</span>
        <button class="btn btn-ghost btn-sm touch-target" data-action="close-drawer">✕</button>
      </div>
      <div class="drawer-body">
        <div style="display:flex; gap:8px; flex-wrap:wrap;">
          <button class="btn btn-accent touch-target">Convert to student</button>
          <button class="btn btn-ghost touch-target">WhatsApp</button>
          <button class="btn btn-ghost touch-target">Schedule meeting</button>
        </div>
        <div class="card" style="padding: 18px;">
          <div class="t-eyebrow" style="margin-bottom:10px;">Activity timeline</div>
          <div class="timeline">
            <div class="tl-item"><div class="tl-when">2d · 16:42</div><div class="tl-what">Submitted <span class="t-mono">techtatwa.in/apply</span></div><div class="tl-actor">auto-assigned → Muhammad T.</div></div>
            <div class="tl-item"><div class="tl-when">1d · 09:30</div><div class="tl-what">WhatsApp intro sent</div><div class="tl-actor">open rate 22%</div></div>
            <div class="tl-item"><div class="tl-when">38h ago</div><div class="tl-what">No response yet.</div></div>
          </div>
        </div>
      </div>
    </aside>
    <aside class="drawer" id="drawer-student" role="dialog" aria-label="Student detail">
      <div class="drawer-head">
        <div class="avatar avatar-lg av-c2" id="stu-avatar">A</div>
        <div style="flex:1; min-width:0;">
          <div class="t-h2" id="stu-name">—</div>
          <div class="t-meta" id="stu-sub">Cohort Sep 02 · joined Aug 14</div>
        </div>
        <span class="pill pill-success"><span class="dot live"></span> Active</span>
        <button class="btn btn-ghost btn-sm touch-target" data-action="close-drawer">✕</button>
      </div>
      <div class="drawer-body">
        <div class="card" style="padding:18px;">
          <div class="t-eyebrow" style="margin-bottom: var(--s-3);">Course progress</div>
          <div class="course-progress" style="height: 8px;"><span style="width: 64%;"></span></div>
          <div class="row" style="margin-top: 8px;"><span class="t-mono t-meta">64% complete</span><span class="spacer"></span><span class="t-meta">W4 of 6</span></div>
        </div>
      </div>
    </aside>
    <aside class="drawer" id="drawer-payslip" role="dialog" aria-label="Payslip">
      <div class="drawer-head">
        <div class="avatar avatar-lg av-c2" id="pay-avatar">V</div>
        <div style="flex:1; min-width:0;">
          <div class="t-h2" id="pay-name">—</div>
          <div class="t-meta" id="pay-sub">—</div>
          <div class="t-mono t-meta" id="pay-file" style="margin-top:4px; font-size:10px;">payslip.pdf</div>
        </div>
        <span class="pill pill-success" id="pay-status-pill"><span class="dot live"></span> Ready</span>
        <button class="btn btn-ghost btn-sm touch-target" data-action="close-drawer">✕</button>
      </div>
      <div class="drawer-body">
        <div class="row" style="gap: 8px;">
          <button class="btn btn-accent touch-target" id="pay-download">Download slip as PDF</button>
          <button class="btn btn-ghost touch-target" data-action="close-drawer">Close</button>
        </div>
        <section class="card" style="padding: 18px;">
          <div class="t-eyebrow" style="margin-bottom: 12px;">Earnings</div>
          <div class="stack-tight">
            <div class="row"><span class="t-meta" style="flex:1;">Hours worked</span><span class="t-mono t-strong" id="pay-hours">0</span></div>
            <div class="row"><span class="t-meta" style="flex:1;">Base rate</span><span class="t-mono" id="pay-rate">—</span></div>
            <div class="row"><span class="t-strong" style="flex:1;">Gross</span><span class="t-mono t-strong" id="pay-gross">₹0</span></div>
          </div>
        </section>
        <section class="card" style="padding: 18px;">
          <div class="t-eyebrow" style="margin-bottom: 12px;">Deductions</div>
          <div class="stack-tight">
            <div class="row"><span class="t-meta" style="flex:1;">PF (6%)</span><span class="t-mono" id="pay-pf">−₹0</span></div>
            <div class="row"><span class="t-meta" style="flex:1;">Professional tax</span><span class="t-mono" id="pay-pt">−₹0</span></div>
            <div class="row" id="pay-tds-row"><span class="t-meta" style="flex:1;">TDS (5% over ₹25k)</span><span class="t-mono" id="pay-tds">−₹0</span></div>
            <div class="row" id="pay-tds-note" style="display:none; border-top:1px dashed var(--rule); padding-top:6px;"><span class="t-mono t-muted" style="font-size:11px; flex:1;" id="pay-tds-note-text"></span></div>
            <div class="row" style="border-top: 1px dashed var(--rule); padding-top: 6px;"><span class="t-strong" style="flex:1;">Total deductions</span><span class="t-mono t-strong" id="pay-total-ded">−₹0</span></div>
          </div>
        </section>
        <section class="card card-accent" style="padding: 18px;">
          <div class="row"><span class="t-h2" style="flex:1;">Net pay</span><span class="t-h2 t-success t-mono" id="pay-net" style="font-size: 28px;">₹0</span></div>
          <div class="divider-rule" style="margin: 8px 0;"></div>
          <div class="t-meta t-mono" id="pay-bank">—</div>
        </section>
      </div>
    </aside>
  `;
  bindRailClicks();
  document.querySelectorAll<HTMLElement>('.bs-item[data-route]').forEach((el) => {
    el.addEventListener('click', () => setRoute(el.dataset.route as never));
  });
  animateKPIs();
}

// Bind-all-once. Listeners are delegated on `document`, so binding once at boot
// (instead of on every paint — the old bug that stacked click handlers and
// caused the payroll print loop / double-fires) is sufficient and safe.
let boundOnce = false;
function bindOnce() {
  if (boundOnce) return;
  boundOnce = true;
  const safe = (fn: () => void, name: string): void => { try { fn(); } catch (e) { console.error('[clientale] bind failed: ' + name, e); } };
  [
    ['bindDrawerClicks', bindDrawerClicks],
    ['bindTopbarActions', bindTopbarActions],
    ['bindAttendance', bindAttendance],
    ['bindPayroll', bindPayroll],
    ['bindPayrollHistory', bindPayrollHistory],
    ['bindStaffAttendance', bindStaffAttendance],
    ['bindCourses', bindCourses],
    ['bindCourseDetail', bindCourseDetail],
    ['bindBatches', bindBatches],
    ['bindStudents', bindStudents],
    ['bindLeadsInbox', bindLeadsInbox],
    ['bindSettings', bindSettings],
    ['bindPipeline', bindPipeline],
    ['bindCommerce', bindCommerce],
    ['bindTreasuryCommerce', bindTreasuryCommerce],
    ['bindSiteContent', bindSiteContent],
    ['bindMembers', bindMembers],
  ].forEach(([n, fn]) => safe(fn as () => void, n));

  document.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    const actionEl = t.closest<HTMLElement>('[data-action]');
    if (actionEl) {
      const a = actionEl.dataset.action;
      if (a === 'goto' && actionEl.dataset.route) { setRoute(actionEl.dataset.route as never); return; }
      if (a === 'toggle-skel') {
        const canvas = document.querySelector<HTMLElement>('main.canvas');
        if (canvas) {
          canvas.style.opacity = '0.45'; canvas.style.transition = 'opacity .18s';
          setTimeout(() => { canvas.style.opacity = ''; }, 900);
        }
        toast('Skeleton view busted · records are live', 'success');
        return;
      }
      // course-new is handled by bindCourses(), which opens the full in-app form.
      if (a === 'pipeline-import') { toast('CSV import — paste a leads CSV, lands with the Firebase endpoint', 'warn'); return; }
      // pipeline-new intentionally not handled here — bindPipeline opens the manual-lead modal.
      if (a === 'treasury-export') { toast('Export: row-level CSV lands with backend billing', 'warn'); return; }
      if (a === 'payout-new') { toast('Batch payout — create a payout batch to queue payments via treasury', 'success'); return; }
      if (a === 'member-invite') { toast('Invite link — enabled when real auth is connected', 'warn'); return; }
      if (a === 'new-period') { toast('New period: W36 starts once W35 is approved', 'warn'); return; }
    }
  });
}

function renderLoginScreen() {
  const app = el('app');
  if (!app) return;
  app.innerHTML = renderLogin();
  bindLogin();
}

function paint() {
  const session = getSession();
  const route = getRoute();
  if (!session || (route as string) === 'login') { renderLoginScreen(); return; }
  renderShell();
}

export function bootApp() {
  setTheme(getTheme());
  const storedTenant = getTenant();
  if (!document.body.dataset.tenant) document.body.dataset.tenant = storedTenant;
  if (!getSession() && location.hash === '') location.hash = '#/login';

  bootRouter();
  onRouteChange(() => paint());
  paint();
  bindOnce();

  document.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.closest('[data-action="close-drawer"]')) closeDrawer();
  });

  window.addEventListener('storage', () => paint());
}

bootApp();
